import type { ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { useRequest } from 'ahooks';
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Steps,
  Switch,
  Table,
  Typography,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import {
  ExamAdminDetailDTO,
  ExamQuestionDTO,
  ExamSaveDTO,
  examController,
  examQuestionController,
} from '../../api';
import DepartmentSelect from '../../components/DepartmentSelect';
import { useCompany } from '../../hooks/company';
import { questionTypeMapping, QuestionTypeTag } from './constants';

interface SelectedQuestion {
  // 编辑回填时来自快照，仅有这几个字段
  question: Partial<Pick<ExamQuestionDTO, 'id' | 'type' | 'content' | 'options'>>;
  score: number;
}

interface BaseInfo {
  title: string;
  description?: string;
  showAnswer?: boolean;
  timeRange?: [Dayjs | null, Dayjs | null] | null;
  durationMinutes?: number;
  maxAttempts: number;
}

interface Props {
  open: boolean;
  // 编辑未锁卷的考试时传入
  exam?: ExamAdminDetailDTO;
  onClose: (refresh: boolean) => void;
}

// 建考试四步向导：基本信息 → 选题设分 → 选部门 → 预览提交
export default ({ open, exam, onClose }: Props) => {
  const { id: companyId } = useCompany();
  const [step, setStep] = useState(0);
  const [form] = Form.useForm<BaseInfo>();
  const [selected, setSelected] = useState<SelectedQuestion[]>(
    (exam?.questions || []).map((item) => ({
      question: {
        id: item.questionId,
        type: item.type,
        content: item.content,
        options: item.options,
      },
      score: item.score || 1,
    })),
  );
  const [departmentIds, setDepartmentIds] = useState<I.ID[]>(
    exam?.departmentIds || [],
  );
  const [baseInfo, setBaseInfo] = useState<BaseInfo>();
  // 及格分数线在选题之后设置：先知道总分再定线
  const [passScore, setPassScore] = useState<number | undefined>(
    exam?.passScore,
  );

  const { data: products } = useRequest(() =>
    examQuestionController.listProducts(companyId),
  );

  const totalScore = useMemo(
    () => selected.reduce((sum, item) => sum + item.score, 0),
    [selected],
  );

  const { loading, run: submit } = useRequest(
    () => {
      const [startAt, endAt] = baseInfo?.timeRange || [];
      const params: ExamSaveDTO = {
        title: baseInfo?.title,
        description: baseInfo?.description,
        showAnswer: baseInfo?.showAnswer,
        passScore,
        startAt: startAt ? startAt.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        endAt: endAt ? endAt.format('YYYY-MM-DDTHH:mm:ss') : undefined,
        durationMinutes: baseInfo?.durationMinutes,
        maxAttempts: baseInfo?.maxAttempts,
        departmentIds: departmentIds as any,
        questions: selected.map((item) => ({
          questionId: item.question.id,
          score: item.score,
        })),
      };
      if (exam?.id) {
        return examController.update(companyId, exam.id, params);
      }
      return examController.create(companyId, params);
    },
    {
      manual: true,
      onSuccess: () => {
        message.success(
          exam ? '考试已更新' : '考试已创建，已向目标部门发送通知',
        );
        onClose(true);
      },
      onError: (e) => message.error(e.message),
    },
  );

  const next = async () => {
    if (step === 0) {
      const values = await form.validateFields();
      setBaseInfo(values);
    }
    if (step === 1) {
      if (!selected.length) {
        message.warning('请至少选择一道题目');
        return;
      }
      if (!passScore) {
        message.warning('请设置及格分数线');
        return;
      }
      if (passScore > totalScore) {
        message.warning(`及格分数线（${passScore}）不能超过总分（${totalScore}）`);
        return;
      }
    }
    if (step === 2 && !departmentIds.length) {
      message.warning('请选择要求参加考试的部门');
      return;
    }
    setStep(step + 1);
  };

  const questionColumns: ProColumns<ExamQuestionDTO>[] = [
    {
      title: '题型',
      dataIndex: 'type',
      width: 90,
      render: (_, record) => <QuestionTypeTag type={record.type} />,
      renderFormItem: () => (
        <Select
          allowClear
          options={Object.entries(questionTypeMapping).map(
            ([value, label]) => ({ value, label }),
          )}
        />
      ),
    },
    {
      title: '产品名称',
      dataIndex: 'productName',
      width: 180,
      renderFormItem: () => (
        <Select
          allowClear
          showSearch
          popupMatchSelectWidth={false}
          options={(products || []).map((item) => ({
            value: item,
            label: item,
          }))}
        />
      ),
    },
    {
      title: '题干',
      dataIndex: 'content',
      ellipsis: true,
      search: { transform: (value) => ({ keyword: value }) },
    },
  ];

  return (
    <Modal
      title={exam ? `编辑考试：${exam.title}` : '新建考试'}
      open={open}
      onCancel={() => onClose(false)}
      width={1000}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={() => onClose(false)}>取消</Button>
          {step > 0 && <Button onClick={() => setStep(step - 1)}>上一步</Button>}
          {step < 3 && (
            <Button type="primary" onClick={next}>
              下一步
            </Button>
          )}
          {step === 3 && (
            <Button type="primary" loading={loading} onClick={submit}>
              {exam ? '保存' : '创建并通知'}
            </Button>
          )}
        </Space>
      }
    >
      <Steps
        current={step}
        size="small"
        style={{ marginBottom: 24 }}
        items={[
          { title: '基本信息' },
          { title: '选择题目' },
          { title: '选择部门' },
          { title: '预览确认' },
        ]}
      />

      <div style={{ display: step === 0 ? 'block' : 'none' }}>
        <Form
          form={form}
          // name 前缀避免与背后列表页搜索表单的字段 id 冲突（label 关联错位）
          name="examWizard"
          layout="vertical"
          initialValues={
            exam
              ? {
                  title: exam.title,
                  description: exam.description,
                  showAnswer: exam.showAnswer,
                  timeRange:
                    exam.startAt || exam.endAt
                      ? [
                          exam.startAt ? dayjs(exam.startAt) : null,
                          exam.endAt ? dayjs(exam.endAt) : null,
                        ]
                      : undefined,
                  durationMinutes: exam.durationMinutes,
                  maxAttempts: exam.maxAttempts,
                }
              : { maxAttempts: 1, showAnswer: false }
          }
        >
          <Form.Item
            name="title"
            label="考试名称"
            rules={[{ required: true, message: '请输入考试名称' }]}
          >
            <Input maxLength={128} placeholder="例如：南通信贷产品知识考试" />
          </Form.Item>
          <Form.Item name="description" label="考试说明">
            <Input.TextArea rows={2} maxLength={1024} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="durationMinutes"
                label="限时（分钟）"
                tooltip="不填则不限时；到时自动交卷"
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="不限时" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxAttempts"
                label="允许作答次数"
                tooltip="重考次数 = 允许作答次数 - 1，成绩取最高分"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={99} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="timeRange"
            label="开始 / 截止时间"
            tooltip="可不填；只填截止时间可在选择后清除开始时间"
          >
            <DatePicker.RangePicker
              showTime={{ format: 'HH:mm' }}
              allowEmpty={[true, true]}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="showAnswer"
            label="考后可见答案"
            tooltip="开启后，交过卷的员工在成绩页可以看到每题的正确答案；可随时开关，实时生效"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </div>

      <div style={{ display: step === 1 ? 'block' : 'none' }}>
        <ProTable<ExamQuestionDTO>
          rowKey="id"
          columns={questionColumns}
          size="small"
          search={{ filterType: 'light' }}
          options={false}
          pagination={{ pageSize: 10 }}
          tableAlertRender={false}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys: selected.map((item) => item.question.id as I.ID),
            onSelect: (record, selectedFlag) => {
              if (selectedFlag) {
                setSelected([...selected, { question: record, score: 1 }]);
              } else {
                setSelected(
                  selected.filter((item) => item.question.id !== record.id),
                );
              }
            },
            onSelectAll: (selectedFlag, _rows, changeRows) => {
              if (selectedFlag) {
                const exists = new Set(selected.map((item) => item.question.id));
                setSelected([
                  ...selected,
                  ...changeRows
                    .filter((row) => !exists.has(row.id))
                    .map((row) => ({ question: row, score: 1 })),
                ]);
              } else {
                const removed = new Set<I.ID | undefined>(
                  changeRows.map((row) => row.id),
                );
                setSelected(
                  selected.filter((item) => !removed.has(item.question.id)),
                );
              }
            },
          }}
          request={async ({ current, pageSize, ...params }) => {
            const data = await examQuestionController.list(
              companyId,
              current,
              pageSize,
              {
                type: (params as any).type,
                productName: (params as any).productName,
                keyword: (params as any).keyword,
              },
            );
            return { data: data.list, success: true, total: data.total };
          }}
        />
        <Typography.Title level={5}>
          已选 {selected.length} 题，总分 {totalScore} 分
          <span style={{ marginLeft: 16, fontSize: 14, fontWeight: 'normal' }}>
            及格分数线：
            <InputNumber
              min={1}
              max={totalScore || undefined}
              value={passScore}
              placeholder="必填"
              onChange={(value) => setPassScore(value ?? undefined)}
            />
          </span>
          <Button
            size="small"
            style={{ marginLeft: 12 }}
            onClick={() => {
              let score: number = 1;
              Modal.confirm({
                title: '批量设置分值',
                content: (
                  <InputNumber
                    min={1}
                    defaultValue={1}
                    onChange={(value) => {
                      score = value || 1;
                    }}
                  />
                ),
                onOk: () =>
                  setSelected((prev) =>
                    prev.map((item) => ({ ...item, score })),
                  ),
              });
            }}
          >
            批量设分
          </Button>
        </Typography.Title>
        <Table<SelectedQuestion>
          rowKey={(item) => String(item.question.id)}
          size="small"
          pagination={false}
          scroll={{ y: 240 }}
          dataSource={selected}
          columns={[
            {
              title: '#',
              width: 50,
              render: (_, __, index) => index + 1,
            },
            {
              title: '题型',
              width: 90,
              render: (_, item) => <QuestionTypeTag type={item.question.type} />,
            },
            {
              title: '题干',
              ellipsis: true,
              render: (_, item) => item.question.content,
            },
            {
              title: '分值',
              width: 110,
              render: (_, item) => (
                <InputNumber
                  min={1}
                  value={item.score}
                  onChange={(value) =>
                    setSelected((prev) =>
                      prev.map((prevItem) =>
                        prevItem.question.id === item.question.id
                          ? { ...prevItem, score: value || 1 }
                          : prevItem,
                      ),
                    )
                  }
                />
              ),
            },
            {
              title: '操作',
              width: 70,
              render: (_, item) => (
                <a
                  onClick={() =>
                    setSelected((prev) =>
                      prev.filter(
                        (prevItem) => prevItem.question.id !== item.question.id,
                      ),
                    )
                  }
                >
                  移除
                </a>
              ),
            },
          ]}
        />
      </div>

      <div style={{ display: step === 2 ? 'block' : 'none' }}>
        <Typography.Paragraph>
          选中部门后，其下所有子部门的在职员工都要求参加，创建后会发送站内消息通知。
        </Typography.Paragraph>
        <DepartmentSelect
          multiple
          value={departmentIds}
          // 去重：TreeSelect 值可能重复
          onChange={(value) =>
            setDepartmentIds(Array.from(new Set<I.ID>(value || [])))
          }
          style={{ width: '100%' }}
          placeholder="选择要求参加考试的部门（可多选）"
        />
      </div>

      {step === 3 && (
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="考试名称" span={2}>
            {baseInfo?.title}
          </Descriptions.Item>
          <Descriptions.Item label="题目数">{selected.length}</Descriptions.Item>
          <Descriptions.Item label="总分">{totalScore}</Descriptions.Item>
          <Descriptions.Item label="及格线">{passScore}</Descriptions.Item>
          <Descriptions.Item label="允许作答次数">
            {baseInfo?.maxAttempts}
          </Descriptions.Item>
          <Descriptions.Item label="开始时间">
            {baseInfo?.timeRange?.[0]
              ? baseInfo.timeRange[0].format('YYYY-MM-DD HH:mm')
              : '不限'}
          </Descriptions.Item>
          <Descriptions.Item label="截止时间">
            {baseInfo?.timeRange?.[1]
              ? baseInfo.timeRange[1].format('YYYY-MM-DD HH:mm')
              : '不限'}
          </Descriptions.Item>
          <Descriptions.Item label="限时">
            {baseInfo?.durationMinutes
              ? `${baseInfo.durationMinutes} 分钟`
              : '不限时'}
          </Descriptions.Item>
          <Descriptions.Item label="考后可见答案">
            {baseInfo?.showAnswer ? '开启' : '关闭'}
          </Descriptions.Item>
          <Descriptions.Item label="目标部门数">
            {departmentIds.length}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
};
